import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Cpu, MemoryStick, HardDrive, Monitor, Tag, Battery, Layers, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { addToCart } from '../api/cartApi';
import { toast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from './ui/dialog';

const ProductCard = ({ product, onAddToCart }) => {
  const [loading, setLoading] = useState(false);
  const [showBulkOrderDialog, setShowBulkOrderDialog] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  // ── Safe values ──────────────────────────────────────────────────
  const productId = product?._id || product?.id || null;
  const stock = Number(product?.stock ?? 0);
  const moq = Number(product?.moq ?? 1);
  const salePrice = Number(product?.basePrice ?? product?.salePrice ?? 0);
  const originalPrice = Number(product?.mrp ?? product?.originalPrice ?? salePrice);
  const discount =
    product?.discountPercentage ??
    (originalPrice > salePrice
      ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
      : 0);
  const condition = product?.condition || 'refurbished';
  const images = product?.images?.length > 0 ? product.images : [product?.image || '/placeholder-image.jpg'];
  const hasSecondImage = images.length > 1;

  // ── Specs from specifications sub-doc ───────────────────────────
  const specs = product?.specifications || {};
  const processor = specs.processor || product?.processor || null;
  const ram = specs.ram || product?.ram || null;
  const storage = specs.storage || product?.storage || null;
  const screenSize = specs.screenSize || product?.screenSize || null;
  const battery = specs.battery || product?.battery || null;
  const generation = specs.generation || product?.generation || null;

  // ── Warranties ───────────────────────────────────────────────────
  const allWarranties = [];
  if (product?.defaultWarranty) {
    allWarranties.push(`${product.defaultWarranty} Warranty`);
  }
  if (product?.warrantyOptions?.length > 0) {
    product.warrantyOptions.forEach(opt => {
      if (opt.duration) allWarranties.push(`+${opt.duration}M Warranty (+₹${opt.price})`);
    });
  }
  const warrantiesToShow = allWarranties.slice(0, 2);

  // ── Add to Cart ──────────────────────────────────────────────────
  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!productId) {
      toast({ title: 'Invalid product', description: 'Product ID missing.', variant: 'destructive' });
      return;
    }
    if (!salePrice || salePrice <= 0) {
      toast({ title: 'Unavailable', description: 'Product price not available.', variant: 'destructive' });
      return;
    }
    if (stock <= 0) {
      toast({ title: 'Out of Stock', description: 'This product is currently unavailable.', variant: 'destructive' });
      return;
    }
    if (1 >= moq) {
      setShowBulkOrderDialog(true);
      return;
    }

    try {
      setLoading(true);
      const response = await addToCart(productId, 1);
      if (response?.success) {
        toast({ title: 'Added to cart', description: `${product?.name} added to your cart.` });
        if (onAddToCart) onAddToCart(product);
      }
    } catch (error) {
      toast({
        title: 'Failed to add to cart',
        description: error?.response?.data?.message || 'Something went wrong.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // ── UI ───────────────────────────────────────────────────────────
  return (
    <Link to={productId ? `/product/${productId}` : '#'} className="block h-full">
      <div
        className="pc-card"
        onMouseEnter={() => hasSecondImage && setImgIndex(1)}
        onMouseLeave={() => setImgIndex(0)}
      >
        {/* ── Image area ── */}
        <div className="pc-img-wrap">
          {/* Condition badge */}
          <span className={`pc-cond-badge ${condition === 'new' ? 'pc-cond-new' : 'pc-cond-refurb'}`}>
            {condition === 'new' ? 'Brand New' : 'Refurbished'}
          </span>

          {/* Discount badge */}
          {discount > 0 && (
            <span className="pc-disc-badge">
              -{discount}%
            </span>
          )}

          {/* Product image with smooth cross-fade to second image */}
          <img
            key={imgIndex}
            src={images[imgIndex]}
            alt={product?.name || 'Laptop'}
            className="pc-img"
            onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
          />

          {/* Second image indicator dots */}
          {hasSecondImage && (
            <div className="pc-img-dots">
              {images.slice(0, Math.min(images.length, 3)).map((_, i) => (
                <span key={i} className={`pc-img-dot ${imgIndex === i ? 'pc-img-dot-active' : ''}`} />
              ))}
            </div>
          )}
        </div>

        {/* ── Card body ── */}
        <div className="pc-body">
          {/* Brand */}
          {product?.brand && (
            <span className="pc-brand">{product.brand}</span>
          )}

          {/* Product name */}
          <h3 className="pc-name">{product?.name || 'Unnamed Product'}</h3>

          {/* Specs chips */}
          <div className="pc-specs">
            {[
              processor && { icon: <Cpu size={12} />, text: processor },
              generation && { icon: <Layers size={12} />, text: generation },
              ram && { icon: <MemoryStick size={12} />, text: ram },
              storage && { icon: <HardDrive size={12} />, text: storage },
              screenSize && { icon: <Monitor size={12} />, text: `${screenSize}"` },
              battery && { icon: <Battery size={12} />, text: battery }
            ].filter(Boolean).slice(0, 6).map((spec, i) => (
              <span key={i} className="pc-spec-chip">
                {spec.icon} <span className="truncate">{spec.text}</span>
              </span>
            ))}
          </div>

          {/* Price row */}
          <div className="pc-price-row">
            <div className="pc-price-block">
              <span className="pc-sale-price">
                ₹{salePrice > 0 ? salePrice.toLocaleString('en-IN') : '—'}
              </span>
              {originalPrice > salePrice && (
                <span className="pc-orig-price">
                  ₹{originalPrice.toLocaleString('en-IN')}
                </span>
              )}
            </div>
            {discount > 0 && (
              <span className="pc-save-tag">
                <Tag size={10} /> Save {discount}%
              </span>
            )}
          </div>

          {/* Warranty */}
          {warrantiesToShow.length > 0 && (
            <div className="flex flex-col gap-1 mt-1">
              {warrantiesToShow.map((wText, idx) => (
                <div key={idx} className="pc-warranty">
                  <ShieldCheck size={13} className="text-green-600 shrink-0" />
                  <span className="truncate">{wText}</span>
                </div>
              ))}
            </div>
          )}

          {/* Stock indicator */}
          {stock > 0 && stock <= 5 && (
            <p className="pc-low-stock">Only {stock} left!</p>
          )}

          {/* Add to Cart */}
          <button
            className={`pc-cart-btn ${stock <= 0 ? 'pc-cart-btn-oos' : ''}`}
            onClick={handleAddToCart}
            disabled={loading || stock <= 0}
          >
            {stock <= 0 ? (
              'Out of Stock'
            ) : (
              <>
                <ShoppingCart size={15} />
                {loading ? 'Adding…' : 'Add to Cart'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Bulk Dialog */}
      <Dialog open={showBulkOrderDialog} onOpenChange={setShowBulkOrderDialog}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Bulk Order Request</DialogTitle>
            <DialogDescription>
              For orders of {moq} or more units, please contact our admin team.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-center">
            <p className="text-lg font-semibold mb-2">Please contact Admin for bulk order</p>
            <p className="text-2xl font-bold text-blue-600">Phone: +91 9845044638</p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
            <Button
              onClick={(e) => { e.stopPropagation(); window.location.href = 'tel:9845044638'; }}
              className="bg-black hover:bg-gray-800 text-white"
            >
              Call Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`
        /* ── Product Card ─────────────────────────────── */
        .pc-card {
          background: var(--pc-card-bg);
          border-radius: 16px;
          box-shadow: 0 2px 12px var(--pc-card-shadow);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          height: 100%;
          transition: transform 0.22s ease, box-shadow 0.22s ease;
          cursor: pointer;
          border: 1px solid var(--pc-card-border);
        }
        .pc-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 32px var(--pc-card-shadow-hover);
        }

        /* ── Image wrapper ────────────────────────────── */
        .pc-img-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 4/3;
          overflow: hidden;
          background: var(--pc-img-bg);
        }
        .pc-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 12px;
          transition: opacity 0.35s ease, transform 0.35s ease;
          animation: pcImgFadeIn 0.3s ease;
        }
        .pc-card:hover .pc-img {
          transform: scale(1.04);
        }
        @keyframes pcImgFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        /* ── Badges ───────────────────────────────────── */
        .pc-cond-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.04em;
          padding: 3px 8px;
          border-radius: 20px;
          z-index: 2;
          text-transform: uppercase;
        }
        .pc-cond-new {
          background: var(--pc-cond-new-bg);
          color: var(--pc-cond-new-color);
        }
        .pc-cond-refurb {
          background: var(--pc-cond-refurb-bg);
          color: var(--pc-cond-refurb-color);
        }
        .pc-disc-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: #fff;
          font-size: 11px;
          font-weight: 800;
          padding: 4px 9px;
          border-radius: 20px;
          z-index: 2;
          box-shadow: 0 2px 8px rgba(239,68,68,0.4);
        }

        /* ── Image indicator dots ─────────────────────── */
        .pc-img-dots {
          position: absolute;
          bottom: 8px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 4px;
          z-index: 2;
        }
        .pc-img-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--pc-img-dot);
          transition: background 0.2s;
        }
        .pc-img-dot-active {
          background: #3b82f6;
          width: 14px;
          border-radius: 3px;
        }

        /* ── Card body ────────────────────────────────── */
        .pc-body {
          padding: 14px 16px 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          flex: 1;
          background: var(--pc-card-bg);
        }

        /* ── Brand label ──────────────────────────────── */
        .pc-brand {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--pc-brand-color);
        }

        /* ── Product name ─────────────────────────────── */
        .pc-name {
          font-size: 14.5px;
          font-weight: 600;
          color: var(--pc-name-color);
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 40px;
          margin: 0;
        }

        /* ── Spec chips ───────────────────────────────── */
        .pc-specs {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 6px;
        }
        .pc-spec-chip {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          font-size: 11px;
          font-weight: 500;
          padding: 4px 6px;
          border-radius: 6px;
          background: var(--pc-spec-chip-bg);
          color: var(--pc-spec-chip-color);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* ── Price row ────────────────────────────────── */
        .pc-price-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 4px;
        }
        .pc-price-block {
          display: flex;
          align-items: baseline;
          gap: 7px;
        }
        .pc-sale-price {
          font-size: 19px;
          font-weight: 800;
          color: var(--pc-sale-price-color);
          line-height: 1;
        }
        .pc-orig-price {
          font-size: 12px;
          font-weight: 400;
          color: var(--pc-orig-price-color);
          text-decoration: line-through;
        }
        .pc-save-tag {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          font-size: 10px;
          font-weight: 700;
          color: var(--pc-save-tag-color);
          background: var(--pc-save-tag-bg);
          padding: 3px 8px;
          border-radius: 20px;
        }

        /* ── Warranty ─────────────────────────────────── */
        .pc-warranty {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          font-weight: 700;
          color: var(--pc-warranty-color);
          margin-top: 2px;
        }

        /* ── Low stock ────────────────────────────────── */
        .pc-low-stock {
          font-size: 10.5px;
          font-weight: 600;
          color: var(--pc-low-stock-color);
          margin: 0;
        }

        /* ── Cart button ──────────────────────────────── */
        .pc-cart-btn {
          margin-top: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          width: 100%;
          padding: 10px 0;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: background 0.18s ease, transform 0.12s ease;
          background: var(--pc-cart-btn-bg);
          color: var(--pc-cart-btn-color);
        }
        .pc-cart-btn:hover:not(:disabled) {
          background: var(--pc-cart-btn-hover);
          transform: scale(1.01);
        }
        .pc-cart-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .pc-cart-btn-oos {
          background: var(--pc-oos-btn-bg) !important;
          color: var(--pc-oos-btn-color) !important;
        }
        .pc-cart-btn-oos:hover {
          background: var(--pc-oos-btn-bg) !important;
          transform: none !important;
        }
      `}</style>
    </Link>
  );
};

export default ProductCard;
